import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GoalPatch } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, cx, ErrorText, Field, Input, PageHeader, Spinner } from "../components/ui.tsx";

type Goal = Awaited<ReturnType<typeof load>>["goals"][number];
const load = () => unwrap(api.me.goals.$get());

const today = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in the browser's zone
const daysLeft = (deadline: string) => Math.round((Date.parse(`${deadline}T00:00:00Z`) - Date.parse(`${today()}T00:00:00Z`)) / 86_400_000);
const fmtDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

/** Your goals: what you're working towards, by when, and how far along. Your assistant keeps the same list. */
export function GoalsPage() {
  const q = useQuery({ queryKey: ["goals"], queryFn: load });
  const [adding, setAdding] = useState(false);
  const [showFinished, setShowFinished] = useState(false);
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const active = q.data.goals.filter((g) => g.status === "active");
  const finished = q.data.goals.filter((g) => g.status !== "active");
  return (
    <>
      <PageHeader
        title="Goals"
        description="What you're working towards, and by when. Your assistant checks in on one of them in your daily briefing and reviews them all with you in the weekly retrospective — you can also just tell it in chat, and it updates this list."
        actions={!adding && active.length < q.data.maxActive ? <Button variant="primary" onClick={() => setAdding(true)}>Add a goal</Button> : undefined}
      />
      {adding && <GoalForm onDone={() => setAdding(false)} />}
      {active.length === 0 && !adding ? (
        <Card className="px-5 py-8 text-center text-sm text-[var(--muted)]">
          No goals yet. Add one here, or tell your assistant what you're aiming for — for example “my goal is to reach 20k monthly visitors by March”.
        </Card>
      ) : (
        <div className="space-y-3">
          {active.map((g) => <GoalCard key={g.id + g.updatedAt} goal={g} />)}
        </div>
      )}
      {finished.length > 0 && (
        <div className="mt-8">
          <button className="text-sm font-medium text-[var(--color-accent)] hover:underline" onClick={() => setShowFinished(!showFinished)}>
            {showFinished ? "Hide" : "Show"} finished goals ({finished.length})
          </button>
          {showFinished && <div className="mt-3 space-y-3">{finished.map((g) => <GoalCard key={g.id + g.updatedAt} goal={g} />)}</div>}
        </div>
      )}
    </>
  );
}

function GoalForm({ goal, onDone }: { goal?: Goal; onDone: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(goal?.title ?? "");
  const [detail, setDetail] = useState(goal?.detail ?? "");
  const [deadline, setDeadline] = useState(goal?.deadline ?? "");
  const save = useMutation({
    mutationFn: () =>
      goal
        ? unwrap(api.me.goals[":id"].$patch({ param: { id: goal.id }, json: { title, detail, deadline: deadline || null } }))
        : unwrap(api.me.goals.$post({ json: { title, detail, deadline: deadline || null } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["goals"] });
      onDone();
    },
  });
  return (
    <Card className="mb-4 p-5">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <Field label="Goal">
            <Input required autoFocus minLength={2} maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Grow organic traffic to 20k visits a month" />
          </Field>
          <Field label="Deadline" hint="Optional">
            <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </Field>
        </div>
        <Field label="What success looks like" hint="Optional — helps your assistant judge progress.">
          <textarea
            rows={2}
            maxLength={2000}
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            className="w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
          />
        </Field>
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={save.isPending || title.trim().length < 2}>{goal ? "Save" : "Add goal"}</Button>
        </div>
      </form>
    </Card>
  );
}

function Deadline({ goal }: { goal: Goal }) {
  if (!goal.deadline) return <span className="text-xs text-[var(--muted)]">No deadline</span>;
  if (goal.status !== "active") return <span className="text-xs text-[var(--muted)]">Deadline {fmtDate(goal.deadline)}</span>;
  const d = daysLeft(goal.deadline);
  return (
    <span className={cx("text-xs", d < 0 ? "font-medium text-red-700" : d <= 7 ? "font-medium text-amber-700" : "text-[var(--muted)]")}>
      {fmtDate(goal.deadline)} · {d < 0 ? `${-d} day${d === -1 ? "" : "s"} overdue` : d === 0 ? "due today" : `${d} day${d === 1 ? "" : "s"} left`}
    </span>
  );
}

function GoalCard({ goal }: { goal: Goal }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"view" | "edit" | "progress" | "history">("view");
  const [progress, setProgress] = useState(goal.progress);
  const [note, setNote] = useState("");
  const patch = useMutation({
    mutationFn: (json: GoalPatch) => unwrap(api.me.goals[":id"].$patch({ param: { id: goal.id }, json })),
    onSuccess: () => {
      setMode("view");
      void qc.invalidateQueries({ queryKey: ["goals"] });
    },
  });
  const remove = useMutation({
    mutationFn: () => unwrap(api.me.goals[":id"].$delete({ param: { id: goal.id } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["goals"] }),
  });
  if (mode === "edit") return <GoalForm goal={goal} onDone={() => setMode("view")} />;
  const finished = goal.status !== "active";
  return (
    <Card className={cx("p-5", finished && "opacity-75")}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1 basis-60">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={cx("font-medium", goal.status === "dropped" && "line-through")}>{goal.title}</h3>
            {goal.status === "done" && <Badge tone="good">Done</Badge>}
            {goal.status === "dropped" && <Badge>Dropped</Badge>}
          </div>
          {goal.detail && <p className="mt-0.5 text-sm text-[var(--muted)]">{goal.detail}</p>}
          <div className="mt-1"><Deadline goal={goal} /></div>
        </div>
        <div className="flex flex-wrap gap-1">
          {!finished ? (
            <>
              <Button variant="ghost" className="h-8" onClick={() => setMode(mode === "progress" ? "view" : "progress")}>Update progress</Button>
              <Button variant="ghost" className="h-8" onClick={() => setMode("edit")}>Edit</Button>
              <Button variant="ghost" className="h-8" disabled={patch.isPending} onClick={() => patch.mutate({ status: "done" })}>Mark done</Button>
              <Button variant="ghost" className="h-8 text-[var(--muted)]" disabled={patch.isPending} onClick={() => confirm(`Drop “${goal.title}”? It stays in your finished goals.`) && patch.mutate({ status: "dropped" })}>Drop</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" className="h-8" disabled={patch.isPending} onClick={() => patch.mutate({ status: "active" })}>Reopen</Button>
              <Button variant="ghost" className="h-8 text-red-700 hover:bg-red-500/10" disabled={remove.isPending} onClick={() => confirm(`Delete “${goal.title}” and its history for good?`) && remove.mutate()}>Delete</Button>
            </>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--panel-2)]" role="progressbar" aria-valuenow={goal.progress} aria-valuemin={0} aria-valuemax={100}>
          <div className={cx("h-full rounded-full", goal.status === "done" ? "bg-emerald-500" : "bg-[var(--color-accent)]")} style={{ width: `${goal.progress}%` }} />
        </div>
        <span className="w-10 text-right text-xs tabular-nums text-[var(--muted)]">{goal.progress}%</span>
      </div>
      {goal.lastNote && mode !== "progress" && (
        <p className="mt-2 text-xs text-[var(--muted)]">
          <span className="font-medium text-[var(--text)]">Latest:</span> {goal.lastNote}
          {goal.lastNoteAt && <> · {new Date(goal.lastNoteAt).toLocaleDateString()}</>}
          <button className="ml-2 text-[var(--color-accent)] hover:underline" onClick={() => setMode(mode === "history" ? "view" : "history")}>{mode === "history" ? "hide history" : "history"}</button>
        </p>
      )}
      {!goal.lastNote && mode === "view" && !finished && (
        <button className="mt-2 text-xs text-[var(--color-accent)] hover:underline" onClick={() => setMode("history")}>history</button>
      )}
      {mode === "progress" && (
        <form
          className="mt-3 space-y-3 rounded-lg bg-[var(--panel-2)] p-4"
          onSubmit={(e) => {
            e.preventDefault();
            patch.mutate({ progress, ...(note.trim() && { note: note.trim() }) });
          }}
        >
          <label className="flex items-center gap-3 text-sm">
            <span className="w-20 shrink-0 font-medium">Progress</span>
            <input type="range" min={0} max={100} step={5} value={progress} onChange={(e) => setProgress(Number(e.target.value))} className="flex-1" />
            <span className="w-10 text-right tabular-nums">{progress}%</span>
          </label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="What happened? (optional)" />
          <ErrorText error={patch.error} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setMode("view")}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={patch.isPending || (progress === goal.progress && !note.trim())}>Save</Button>
          </div>
        </form>
      )}
      {mode === "history" && <History goalId={goal.id} />}
      {mode === "view" && <ErrorText error={patch.error ?? remove.error} />}
    </Card>
  );
}

function History({ goalId }: { goalId: string }) {
  const q = useQuery({ queryKey: ["goal-history", goalId], queryFn: () => unwrap(api.me.goals[":id"].history.$get({ param: { id: goalId } })) });
  if (q.isPending) return <div className="py-3"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  return (
    <ul className="mt-3 space-y-1.5 border-t border-[var(--line)] pt-3 text-xs">
      {q.data.updates.map((u) => {
        const c = u.changes as Record<string, unknown>;
        const what = [
          c.created && "Added",
          c.status && `Marked ${String(c.status)}`,
          c.deadline !== undefined && !c.created && `Deadline → ${c.deadline ? fmtDate(String(c.deadline)) : "none"}`,
          c.title && `Renamed to “${String(c.title)}”`,
          u.progress !== null && c.progress !== undefined && `${u.progress}%`,
        ].filter(Boolean);
        return (
          <li key={u.id} className="flex gap-3">
            <span className="w-24 shrink-0 text-[var(--muted)]">{new Date(u.createdAt).toLocaleDateString()}</span>
            <span className="min-w-0 flex-1">
              {what.join(" · ")}
              {u.note && <>{what.length ? " — " : ""}{u.note}</>}
              <span className="text-[var(--muted)]"> · {u.by === "assistant" ? "by your assistant" : "by you"}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
